// Landing page motion. Every visual change is a class toggle; CSS animates only transform and opacity.
// With prefers-reduced-motion nothing here runs and the page keeps its static, settled state.

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const jitter = (base: number, spread: number) => base + Math.round(Math.random() * spread);

/** Reveals `[data-reveal]` blocks once, and marks them `is-inview` so their CSS loops only run while visible. */
function initReveals(): void {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        e.target.classList.toggle('is-inview', e.isIntersecting);
        if (e.isIntersecting) e.target.classList.add('is-revealed');
      }
    },
    { threshold: 0.25, rootMargin: '0px 0px -8% 0px' },
  );
  document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
}

/** Restarts a one-shot CSS animation class; the class removes itself when the animation ends. */
function flash(el: Element, cls: string): void {
  el.classList.remove(cls);
  requestAnimationFrame(() => el.classList.add(cls));
}

/** The hero conversation: chips cross the channel in turn, each receiver reads, then the spec settles and both approve. */
function runStage(stage: HTMLElement): void {
  const sideEl = (k: string) => stage.querySelector<HTMLElement>(`.env[data-side="${k}"]`);
  const envA = sideEl('a');
  const envB = sideEl('b');
  const pulse = stage.querySelector<HTMLElement>('.channel-pulse');
  const spec = stage.querySelector<HTMLElement>('.chip[data-dir="spec"]');
  const chips = [...stage.querySelectorAll<HTMLElement>('.chip[data-dir="ab"], .chip[data-dir="ba"]')];
  if (!envA || !envB || !pulse || !spec || chips.length === 0) return;

  // Pause the loop (and, through .is-paused, every CSS loop in the stage) while it is offscreen or the tab is hidden.
  let onScreen = false;
  let wake: (() => void) | null = null;
  const update = () => {
    const running = onScreen && !document.hidden;
    stage.classList.toggle('is-paused', !running);
    if (running && wake) {
      wake();
      wake = null;
    }
  };
  new IntersectionObserver(([e]) => {
    onScreen = e?.isIntersecting ?? false;
    update();
  }).observe(stage);
  document.addEventListener('visibilitychange', update);
  const running = () => onScreen && !document.hidden;
  const step = async (ms: number) => {
    await sleep(ms);
    while (!running()) await new Promise<void>((resolve) => (wake = resolve));
  };

  pulse.addEventListener('animationend', () => pulse.classList.remove('is-on'));
  for (const c of chips) c.addEventListener('animationend', () => c.classList.remove('is-crossing'));

  const look = (env: HTMLElement, line: string | undefined) => {
    env.querySelectorAll('.ln.is-look').forEach((l) => l.classList.remove('is-look'));
    const target = line ? env.querySelector(`.ln[data-ln="${line}"]`) : null;
    const caret = env.querySelector('.caret');
    if (target) {
      target.classList.add('is-look');
      if (caret) target.append(caret);
    }
  };

  const loop = async () => {
    stage.classList.add('is-live');
    await step(600);
    for (;;) {
      for (const chip of chips) {
        const ab = chip.dataset.dir === 'ab';
        const from = ab ? envA : envB;
        const to = ab ? envB : envA;
        const dur = Number(chip.dataset.dur) || 2400;

        from.classList.add('is-writing');
        await step(jitter(700, 400));
        from.classList.remove('is-writing');

        chip.classList.add('is-crossing');
        await step(Math.round(dur * 0.47)); // the chip reaches the line
        flash(pulse, 'is-on');
        await step(Math.round(dur * 0.53));

        to.classList.add('is-reading');
        look(to, chip.dataset.look);
        await step(jitter(900, 400));
        to.classList.remove('is-reading');
        await step(jitter(200, 400));
      }

      flash(pulse, 'is-on');
      spec.classList.add('is-settled');
      await step(1300);
      envA.classList.add('is-approved');
      await step(jitter(800, 400));
      envB.classList.add('is-approved');
      await step(3400);

      stage.classList.add('is-resetting');
      await step(800);
      spec.classList.remove('is-settled');
      for (const env of [envA, envB]) {
        env.classList.remove('is-approved');
        look(env, undefined);
      }
      stage.classList.remove('is-resetting');
      await step(jitter(700, 500));
    }
  };
  void loop();
}

export function initMotion(): void {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;
  document.body.classList.add('motion');
  initReveals();
  const stage = document.querySelector<HTMLElement>('[data-stage]');
  if (stage) runStage(stage);
  // Switching to reduced motion mid-visit drops back to the static state; the CSS media query stops what is running.
  reduce.addEventListener('change', () => {
    if (reduce.matches) document.body.classList.remove('motion');
  });
}
